import { UploadFileFn } from '../../../media/use-upload-media';
import { getFileExtension, isFileObject } from '../../../../utils';
import { ClientHandler } from '../../../../types';
import { AnyCreativityConfig, CreativityType } from '../../../../types/banner';
import type { RoutedFormData } from '../../../../types/templates';

/**
 * Detects file objects in formData, uploads them in parallel,
 * and returns a new formData with file fields replaced by { originalUrl }.
 */
export async function uploadFormFiles(
  formData: RoutedFormData,
  uploadFn: UploadFileFn,
  clientId: string,
  bannerId: string
): Promise<RoutedFormData> {
  const formDataCopy: RoutedFormData = { ...formData };
  const uploadPromises: Promise<void>[] = [];

  for (const [fieldKey, value] of Object.entries(formData)) {
    if (!isFileObject(value)) continue;

    uploadPromises.push(
      (async () => {
        const fileExtension = getFileExtension(value.file.name);
        const result = await uploadFn('video', fileExtension, value.file, clientId, bannerId, fieldKey);

        if (result?.publicUrl) {
          formDataCopy[fieldKey] = { originalUrl: result.publicUrl };
        }
      })()
    );
  }

  await Promise.all(uploadPromises);
  return formDataCopy;
}

/**
 * Applies the client handler's transformBannerType if available.
 */
export function resolveBannerType(
  clientHandler: ClientHandler,
  formData: RoutedFormData,
  creativityType: CreativityType
): CreativityType {
  if (!clientHandler.transformBannerType) return creativityType;

  return clientHandler.transformBannerType(
    { ...formData, _creativityType: creativityType },
    creativityType
  ) as CreativityType;
}

/**
 * Cleans form data using the client handler if available, otherwise returns the form data without the title field.
 */
export function buildCleanConfig(
  clientHandler: ClientHandler,
  formData: RoutedFormData,
  creativityType: CreativityType,
  finalBannerType: CreativityType
): AnyCreativityConfig {
  if (clientHandler.cleanFormData) {
    const cleaned = clientHandler.cleanFormData({ ...formData, _creativityType: creativityType }, finalBannerType);
    return cleaned as object as AnyCreativityConfig;
  }

  const { title: _title, ...config } = formData;
  return config as object as AnyCreativityConfig;
}
